import React from 'react';
import { motion } from 'framer-motion';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CreativeButton from './CreativeButton';

const iconBadge =
  "w-10 h-10 rounded-full bg-white/10 dark:bg-white/5 flex items-center justify-center flex-shrink-0";

const socialBadge =
  "w-11 h-11 rounded-full bg-white/10 dark:bg-white/5 text-white flex items-center justify-center transition-colors hover:bg-[#e1ae48] hover:text-black";

// py-2 keeps the row ~40px tall so it is comfortably tappable on touch
// devices; the underline still hugs the text itself.
const navLink =
  "group relative inline-flex w-fit items-center gap-1.5 py-2 text-white/70 dark:text-[#9CA3AF] hover:text-white dark:hover:text-white transition-colors";

const NavLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} className={navLink}>
    <span className="relative">
      {children}
      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-current transition-all duration-300 group-hover:w-full" />
    </span>
  </a>
);

const Footer: React.FC = () => {
  return (
    <div className="overflow-hidden px-4 sm:px-6 md:px-5 pt-10 bg-white dark:bg-[#121212]">
      <motion.footer
        className="relative overflow-hidden bg-[#172b81] dark:bg-[#1E1E1E] text-white dark:text-[#E5E7EB] rounded-t-[32px] sm:rounded-t-[40px]"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.2 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 md:px-14 pt-14 md:pt-20 pb-10">

          {/* CTA row */}
          <motion.div
            className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-12 md:pb-16 border-b border-white/10 dark:border-white/5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#e1ae48] font-bold mb-4">Let's talk cold chain</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight max-w-xl leading-[1.1]">
                Ready to upgrade your refrigeration?
              </h2>
            </div>
            <CreativeButton
              as="a"
              href="/#contact"
              fillColor="#e1ae48"
              hoverTextClassName="text-black"
              className="gap-3 self-start bg-white text-[#172b81] font-bold px-7 py-4 flex-shrink-0"
            >
              Get a Quote
              <ArrowOutwardIcon
                fontSize="small"
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </CreativeButton>
          </motion.div>

          {/* Link grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-12 py-12 md:py-16">

            {/* Brand + Social */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.2 }}
              transition={{ delay: 0.1, duration: 0.7 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md overflow-hidden flex-shrink-0 p-2">
                  <img
                    src="/images/viber.webp"
                    alt="Viber QR Code"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain"
                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "https://placehold.co/128x128/cccccc/333333?text=QR";
                    }}
                  />
                </div>
                <p className="font-bold text-lg leading-tight">Join our Viber Newsletter</p>
              </div>
              <p className="text-sm text-white/70 dark:text-[#9CA3AF] leading-relaxed max-w-xs mb-6">
                Scan the code to get product updates, promos, and cold chain tips straight to your phone.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=100094759338735"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  className={socialBadge}
                >
                  <FacebookIcon fontSize="small" />
                </a>
                <a
                  href="https://www.instagram.com/rkandkimports"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className={socialBadge}
                >
                  <InstagramIcon fontSize="small" />
                </a>
              </div>
            </motion.div>

            {/* Company links */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.2 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              <p className="text-[10px] uppercase font-black tracking-[0.3em] text-white/50 dark:text-[#9CA3AF] mb-5">Company</p>
              <ul className="space-y-1 text-sm">
                <li><NavLink href="/">Home</NavLink></li>
                <li><NavLink href="/#about">About Us</NavLink></li>
                <li><NavLink href="/products">Products</NavLink></li>
              </ul>
            </motion.div>

            {/* Support links */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.2 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              <p className="text-[10px] uppercase font-black tracking-[0.3em] text-white/50 dark:text-[#9CA3AF] mb-5">Support</p>
              <ul className="space-y-1 text-sm">
                <li><NavLink href="/#services">Services</NavLink></li>
                <li><NavLink href="/#faq">FAQs</NavLink></li>
                <li><NavLink href="/#contact">Contact Us</NavLink></li>
                <li><NavLink href="/privacy-policy">Privacy Policy</NavLink></li>
              </ul>
            </motion.div>

            {/* Contact */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.2 }}
              transition={{ delay: 0.4, duration: 0.7 }}
            >
              <p className="text-[10px] uppercase font-black tracking-[0.3em] text-white/50 dark:text-[#9CA3AF] mb-5">Get in Touch</p>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className={iconBadge}>
                    <LocationOnIcon fontSize="small" />
                  </span>
                  <p className="text-white/80 dark:text-[#9CA3AF] pt-2.5">#1411 Cavite St., Manila, Philippines</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className={iconBadge}>
                    <PhoneIcon fontSize="small" />
                  </span>
                  <div className="text-white/80 dark:text-[#9CA3AF] pt-2">
                    <p><a href="tel:+639778361411" className="inline-block py-1.5 text-white dark:text-[#E5E7EB] hover:text-[#e1ae48] transition-colors">+63 977 836 1411</a> — Operations</p>
                    <p><a href="tel:+639778031411" className="inline-block py-1.5 text-white dark:text-[#E5E7EB] hover:text-[#e1ae48] transition-colors">+63 977 803 1411</a> — Marketing</p>
                    <p><a href="tel:+639778021411" className="inline-block py-1.5 text-white dark:text-[#E5E7EB] hover:text-[#e1ae48] transition-colors">+63 977 802 1411</a> — Sales</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>

          {/* Wordmark + back to top */}
          <div className="mt-10 md:mt-14 flex items-end justify-between gap-6">
            <div className="flex items-center gap-4 sm:gap-6">
              <img
                src="/images/rknk_logo.webp"
                alt="RK & K Logo"
                className="h-[clamp(2rem,7vw,5rem)] w-auto opacity-20 flex-shrink-0"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://placehold.co/40x40/cccccc/333333?text=Logo";
                }}
              />
              <span className="text-[clamp(2.5rem,10vw,7rem)] font-black leading-none tracking-tight text-white/10 select-none">
                RK&K
              </span>
            </div>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Back to top"
              className="w-12 h-12 rounded-full border border-white/15 dark:border-white/10 flex items-center justify-center text-white/70 hover:text-black hover:bg-[#e1ae48] hover:border-[#e1ae48] transition-colors flex-shrink-0"
            >
              <KeyboardArrowUpIcon />
            </button>
          </div>

          {/* Bottom bar */}
          <div className="mt-8 pt-8 border-t border-white/10 dark:border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/60 dark:text-[#9CA3AF]">
            <p>&copy; {new Date().getFullYear()} RK & K Import - Export Inc. All rights reserved.</p>
            <p>
              Powered by{' '}
              <a
                href="https://working-ants.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block py-1.5 font-semibold text-white/80 dark:text-[#E5E7EB] hover:text-white transition-colors"
              >
                Working Ants Innovations
              </a>
            </p>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default Footer;
